import { TextField, type TextFieldProps } from '@/components/inputs/TextField'
import { type KeyboardEvent, useCallback, useState } from 'react'
import { dateFormatters, parseAmericanDate } from '@/utils/date-util'
import { masks } from '@/utils/input-mask-util'
import { mergeProps } from 'react-aria'

export interface DateFieldProps
  extends Omit<TextFieldProps, 'value' | 'defaultValue' | 'onChange' | 'mask'> {
  /**
   * The current value.
   */
  value?: Date | null
  /**
   * The default value if using uncontrolled.
   */
  defaultValue?: Date | null
  /**
   * Change handler.
   *
   * @param newValue
   */
  onChange?: (newValue: Date | null) => unknown
}

/**
 * Similar to a {@link TextField}, but only allows valid dates as text.
 */
export function DateField({
  value,
  defaultValue,
  onChange,
  ...props
}: DateFieldProps) {
  const [internalDate, setInternalDate] = useState(
    (value === undefined ? defaultValue : value) ?? null,
  )
  const [stringValue, setStringValue] = useState(() =>
    internalDate ? dateFormatters.dateAmerican.format(internalDate) : '',
  )

  if (value !== undefined) {
    // Input is controlled, and the prop changed, so change the internal state.
    if (value !== internalDate) {
      // Only reset the string value if we're changing to a valid date.
      if (value !== null) {
        setStringValue(value ? dateFormatters.dateAmerican.format(value) : '')
      }

      setInternalDate(value)
    }
  }

  const changeDate = useCallback(
    (date: Date | null) => {
      setInternalDate(date)
      if (value === undefined || value !== date) {
        onChange?.(date)
      }
    },
    [onChange, value],
  )

  const changeString = useCallback(
    (newString: string) => {
      const parsed = parseAmericanDate(newString)
      setStringValue(newString)
      changeDate(parsed)
    },
    [changeDate],
  )

  const finalize = useCallback(() => {
    // Ensures that, when we blur or hit enter, any pivoting is reflected in the
    // string value.
    const parsed = parseAmericanDate(stringValue, 49)
    if (parsed) {
      const formatted = dateFormatters.dateAmerican.format(parsed)
      if (formatted !== stringValue) {
        changeString(formatted)
      }
    }
  }, [changeString, stringValue])

  const keyDown = useCallback(
    (evt: KeyboardEvent) => {
      if (evt.key === 'Enter') {
        finalize()
      }
    },
    [finalize],
  )

  return (
    <TextField
      {...mergeProps(props, {
        onBlur: finalize,
        // Using `capture` to make sure the
        // date is finalized before handling any potential
        // form submission.
        onKeyDownCapture: keyDown,
      })}
      mask={masks.americanDate}
      value={stringValue}
      onChange={changeString}
    />
  )
}
